import { API_URLS } from '../constants';
import { authenticatedFetch } from './api';

interface EnhanceResponse {
  status: 'success' | 'error';
  data?: {
    prompt: string;
    run_id: string;
  };
  message?: string;
  error_code?: string;
  details?: {
    detail: string;
  };
}

// Callback function type for streaming chunks
type StreamCallback = (chunk: string) => void;
type StreamCompleteCallback = (fullResponse: string) => void;
type StreamErrorCallback = (error: Error) => void;

interface StreamCallbacks {
  onChunk?: StreamCallback;
  onComplete?: StreamCompleteCallback;
  onError?: StreamErrorCallback;
}

export async function enhancePrompt(promptText: string): Promise<string> {
  try {
  const response = await authenticatedFetch(API_URLS.AGENT.ENHANCE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt: promptText
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data: EnhanceResponse = await response.json();
    
    if (data.status === 'success' && data.data?.prompt) {
      return data.data.prompt;
    } else {
      throw new Error(data.message || 'Enhancement failed');
    }
  } catch (error) {
    console.error('Failed to enhance prompt:', error);
    throw error;
  }
}

export async function enhancePromptStreaming(
  promptText: string, 
  callbacks: StreamCallbacks
): Promise<void> {
  try {
    // Use your backend API endpoint for streaming
    const res = await authenticatedFetch(API_URLS.AGENT.ENHANCE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Stream': 'true',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify({ prompt: promptText })
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(`Agent API error: ${res.status} ${text}`);
      if (callbacks.onError) callbacks.onError(err);
      throw err;
    }

    if (!res.body) {
      const text = await res.text();
      if (callbacks.onChunk) callbacks.onChunk(text);
      if (callbacks.onComplete) callbacks.onComplete(text);
      return;
    }

    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buffer = '';
    let full = '';

    const pushFrame = (frame: string) => {
      frame = frame.trim();
      if (!frame) return;

      // Handle SSE data: lines
      if (frame.startsWith('data:')) {
        const dataLines = frame.split(/\r?\n/).map(l => l.replace(/^data:\s?/, ''));
        const joined = dataLines.join('\n');
        try {
          const parsed = JSON.parse(joined);
          const content = parsed?.data?.content ?? parsed?.content ?? '';
          if (content) {
            full += content;
            if (callbacks.onChunk) callbacks.onChunk(content);
          }
          return;
        } catch {
          // not JSON: emit raw
          full += joined;
          if (callbacks.onChunk) callbacks.onChunk(joined);
          return;
        }
      }

      // Try JSON object
      try {
        const parsed = JSON.parse(frame);
        const content = parsed?.data?.content ?? parsed?.content ?? '';
        if (content) {
          full += content;
          if (callbacks.onChunk) callbacks.onChunk(content);
        } else if (typeof parsed === 'string') {
          full += parsed;
          if (callbacks.onChunk) callbacks.onChunk(parsed);
        }
      } catch {
        // Fallback: raw text
        full += frame;
        if (callbacks.onChunk) callbacks.onChunk(frame);
      }
    };

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += dec.decode(value, { stream: true });

        // First, split SSE-style frames by double newline
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          pushFrame(frame);
        }

        // If no SSE boundary, try to extract complete JSON objects by matching braces
        const firstBrace = buffer.indexOf('{');
        if (firstBrace !== -1) {
          let depth = 0;
          for (let i = firstBrace; i < buffer.length; i++) {
            const ch = buffer[i];
            if (ch === '{') depth++;
            else if (ch === '}') depth--;
            if (depth === 0) {
              const jsonText = buffer.slice(firstBrace, i + 1);
              buffer = buffer.slice(i + 1);
              pushFrame(jsonText);
              break; // re-evaluate buffer in next loop iteration
            }
          }
        }
      }

      // Flush any remaining buffer
      if (buffer.trim()) pushFrame(buffer);

      if (callbacks.onComplete) callbacks.onComplete(full);
    } finally {
      reader.releaseLock();
    }
  } catch (err) {
    if (callbacks.onError) callbacks.onError(err instanceof Error ? err : new Error(String(err)));
    throw err;
  }

}

// Alternative streaming function that returns a readable stream
export async function enhancePromptStreamingGenerator(promptText: string): Promise<ReadableStream<string>> {
  const response = await authenticatedFetch(API_URLS.AGENT.ENHANCE, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Stream': 'true',
      'Cache-Control': 'no-cache'
    },
    body: JSON.stringify({
      prompt: promptText
    })
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('No response body for streaming');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  return new ReadableStream<string>({
    start(controller) {
      let sseBuffer = '';

      function pump(): Promise<void> {
        return reader.read().then(({ done, value }) => {
          if (done) {
            controller.close();
            return;
          }

          const chunk = decoder.decode(value, { stream: true });
          sseBuffer += chunk;

          let idx;
          while ((idx = sseBuffer.indexOf('\n\n')) !== -1) {
            const frame = sseBuffer.slice(0, idx).trim();
            sseBuffer = sseBuffer.slice(idx + 2);

            const lines = frame.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
            const dataLines = lines.filter(l => l.startsWith('data:'));
            if (dataLines.length === 0) continue;

            const jsonText = dataLines.map(l => l.replace(/^data:\s?/, '')).join('\n');
            try {
              const parsed = JSON.parse(jsonText);
              if (parsed && parsed.type === 'chunk' && parsed.data && typeof parsed.data.content === 'string') {
                controller.enqueue(parsed.data.content);
              }
            } catch (e) {
              // partial frame: put back and wait for next pump
              sseBuffer = frame + '\n\n' + sseBuffer;
              break;
            }
          }

          return pump();
        });
      }

      return pump();
    }
  });
}
